import React, { useState, useCallback } from "react";
import { X, AlertTriangle } from "lucide-react";

// Global confirm state — single shared modal across the app.
// Works inside sandboxed iframes (Emergent preview) where window.confirm() is blocked.
let resolver = null;
let setter = null;

export function confirmDialog({ title = "Are you sure?", message = "", confirmLabel = "Delete", cancelLabel = "Cancel", destructive = true } = {}) {
  if (!setter) {
    // Fallback to native confirm if provider not mounted
    return Promise.resolve(window.confirm(message || title));
  }
  return new Promise((resolve) => {
    resolver = resolve;
    setter({ open: true, title, message, confirmLabel, cancelLabel, destructive });
  });
}

export function ConfirmProvider() {
  const [state, setState] = useState({ open: false, title: "", message: "", confirmLabel: "Delete", cancelLabel: "Cancel", destructive: true });
  setter = setState;

  const close = useCallback((value) => {
    setState((s) => ({ ...s, open: false }));
    if (resolver) {
      resolver(value);
      resolver = null;
    }
  }, []);

  if (!state.open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" onClick={() => close(false)} data-testid="confirm-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--surface)] border border-neutral-800 p-6 max-w-md w-full">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            {state.destructive && (
              <div className="w-9 h-9 border border-[var(--accent)] flex items-center justify-center shrink-0">
                <AlertTriangle size={16} className="text-[var(--accent)]" />
              </div>
            )}
            <h3 className="font-headline text-xl uppercase">{state.title}</h3>
          </div>
          <button onClick={() => close(false)} data-testid="confirm-close"><X size={18} /></button>
        </div>
        {state.message && <p className="text-neutral-300 text-sm mb-6">{state.message}</p>}
        <div className="flex gap-2 justify-end">
          <button onClick={() => close(false)} className="btn-ghost" data-testid="confirm-cancel">{state.cancelLabel}</button>
          <button
            onClick={() => close(true)}
            className={state.destructive ? "btn-primary" : "btn-primary"}
            data-testid="confirm-ok"
            autoFocus
          >
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
